import React from 'react';
import { X, Shield, Lock, Eye, Users, Database, Mail, Phone, MapPin, FileText, Cookie, AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const PrivacyPolicy = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const sections = [
    {
      id: 'intro',
      title: 'Introdução',
      icon: Shield,
      content: `A FERNANDO TECH ("nós", "nosso" ou "empresa"), com domínio em fernandotech.com.br, está comprometida com a proteção da privacidade e dos dados pessoais de nossos usuários, clientes e visitantes.

Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).`
    },
    {
      id: 'coleta',
      title: 'Dados Pessoais Coletados',
      icon: Database,
      content: `Coletamos os seguintes tipos de dados pessoais:

• **Dados de identificação:** Nome completo, e-mail, telefone
• **Dados de navegação:** Endereço IP, tipo de navegador, páginas visitadas, tempo de permanência
• **Dados de comunicação:** Mensagens enviadas pelo formulário de contato, histórico de atendimento
• **Dados de dispositivo:** Sistema operacional, resolução de tela, identificadores de dispositivo

Os dados são coletados quando você:
- Preenche nosso formulário de contato
- Navega em nosso site
- Entra em contato via WhatsApp
- Utiliza nossos serviços de suporte técnico`
    },
    {
      id: 'finalidade',
      title: 'Finalidade do Tratamento',
      icon: Eye,
      content: `Utilizamos seus dados pessoais para as seguintes finalidades:

• **Prestação de serviços:** Atender solicitações de suporte técnico, orçamentos e consultorias
• **Comunicação:** Responder suas mensagens e dúvidas
• **Melhoria do site:** Analisar o uso do site para aprimorar a experiência do usuário
• **Marketing:** Enviar informações sobre nossos serviços (apenas com seu consentimento)
• **Obrigações legais:** Cumprir obrigações fiscais e regulatórias
• **Segurança:** Proteger nossos sistemas e prevenir fraudes`
    },
    {
      id: 'cookies',
      title: 'Uso de Cookies',
      icon: Cookie,
      content: `Utilizamos cookies e tecnologias semelhantes para:

**Cookies Essenciais (Sempre ativos)**
- Funcionamento básico do site
- Segurança e autenticação
- Preferências de consentimento

**Cookies de Análise (Com consentimento)**
- Google Analytics: Estatísticas de uso do site
- Métricas de desempenho e navegação

**Cookies de Marketing (Com consentimento)**
- Google Ads: Remarketing e publicidade direcionada
- Meta Pixel (Facebook/Instagram): Análise de campanhas
- Personalização de anúncios

**Cookies de Funcionalidade (Com consentimento)**
- Preferências do usuário
- Personalização de conteúdo

Você pode gerenciar suas preferências de cookies a qualquer momento através do banner de consentimento ou limpando os cookies do seu navegador.`
    },
    {
      id: 'compartilhamento',
      title: 'Compartilhamento de Dados',
      icon: Users,
      content: `Podemos compartilhar seus dados com:

**Parceiros de Tecnologia:**
- Google LLC (Analytics, Ads)
- Meta Platforms Inc. (Facebook, Instagram, WhatsApp)
- Provedores de hospedagem e infraestrutura

**Autoridades Públicas:**
- Quando exigido por lei ou ordem judicial

**Prestadores de Serviço:**
- Empresas que nos auxiliam na prestação de serviços (sob contrato de confidencialidade)

**Importante:** Não vendemos, alugamos ou comercializamos seus dados pessoais com terceiros para fins de marketing sem seu consentimento expresso.`
    },
    {
      id: 'direitos',
      title: 'Seus Direitos (LGPD)',
      icon: CheckCircle,
      content: `De acordo com a LGPD, você tem os seguintes direitos:

• **Confirmação:** Confirmar a existência de tratamento de seus dados
• **Acesso:** Acessar seus dados pessoais
• **Correção:** Corrigir dados incompletos, inexatos ou desatualizados
• **Anonimização:** Solicitar anonimização, bloqueio ou eliminação de dados
• **Portabilidade:** Solicitar a portabilidade dos dados para outro fornecedor
• **Eliminação:** Solicitar a eliminação dos dados tratados com consentimento
• **Informação:** Ser informado sobre o compartilhamento de dados
• **Revogação:** Revogar o consentimento a qualquer momento
• **Oposição:** Opor-se ao tratamento em casos de descumprimento da LGPD

Para exercer seus direitos, entre em contato conosco através dos canais indicados ao final desta política.`
    },
    {
      id: 'seguranca',
      title: 'Segurança dos Dados',
      icon: Lock,
      content: `Implementamos medidas técnicas e organizacionais para proteger seus dados:

• Criptografia de dados em trânsito (HTTPS/SSL)
• Controle de acesso restrito aos dados
• Monitoramento de segurança
• Backup regular dos dados
• Treinamento da equipe em proteção de dados
• Revisão periódica das práticas de segurança

Apesar de nossos esforços, nenhum sistema é 100% seguro. Em caso de incidente de segurança que possa causar risco ou dano relevante, notificaremos você e a Autoridade Nacional de Proteção de Dados (ANPD).`
    },
    {
      id: 'retencao',
      title: 'Retenção de Dados',
      icon: FileText,
      content: `Mantemos seus dados pessoais pelo tempo necessário para:

• Cumprir as finalidades descritas nesta política
• Atender obrigações legais e regulatórias
• Exercer direitos em processos judiciais ou administrativos

**Prazos específicos:**
- Dados de navegação: até 2 anos
- Dados de contato: até 5 anos após último contato
- Dados fiscais: conforme legislação tributária

Após o período de retenção, os dados serão eliminados ou anonimizados.`
    }
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/20 overflow-hidden flex flex-col">
        
        {/* Header fixo */}
        <div className="sticky top-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-cyan-500/20 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-cyan-500/20 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Política de Privacidade</h1>
                <p className="text-sm text-cyan-300">FERNANDO TECH - fernandotech.com.br</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-400 hover:text-white" />
            </button>
          </div>
        </div>

        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8">
          {/* Data de atualização */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4">
            <p className="text-cyan-300 text-sm">
              <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Seções */}
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-cyan-500/10 p-2 rounded-lg">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h2 className="text-lg font-bold text-white">{section.title}</h2>
                </div>
                <div className="text-gray-300 leading-relaxed whitespace-pre-line text-sm">
                  {section.content.split('**').map((part, index) => 
                    index % 2 === 1 ? (
                      <strong key={index} className="text-cyan-400">{part}</strong>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </div>
              </div>
            );
          })}

          {/* Contato do Responsável */}
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-cyan-500/20 p-2 rounded-lg">
                <Mail className="w-5 h-5 text-cyan-400" />
              </div>
              <h2 className="text-lg font-bold text-white">Contato do Responsável</h2>
            </div>
            <p className="text-gray-300 mb-4 text-sm">
              Para exercer seus direitos ou esclarecer dúvidas sobre esta Política de Privacidade, entre em contato:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-lg">
                <Mail className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-xs text-gray-400">E-mail</p>
                  <a href="mailto:contato@fernandotech.com.br" className="text-cyan-400 hover:underline text-sm">
                    contato@fernandotech.com.br
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-lg">
                <Phone className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-xs text-gray-400">Telefone/WhatsApp</p>
                  <a href="tel:+5516991378312" className="text-cyan-400 hover:underline text-sm">
                    (16) 99137-8312
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-slate-800/50 p-3 rounded-lg md:col-span-2">
                <MapPin className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-xs text-gray-400">Localização</p>
                  <p className="text-white text-sm">Ribeirão Preto, São Paulo - Brasil</p>
                </div>
              </div>
            </div>
          </div>

          {/* Aviso final */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-200 text-sm">
                Esta Política de Privacidade pode ser atualizada periodicamente. Recomendamos que você revise esta página regularmente para se manter informado sobre nossas práticas de privacidade.
              </p>
            </div>
          </div>
        </div>

        {/* Footer fixo */}
        <div className="sticky bottom-0 bg-slate-900/95 border-t border-slate-700/50 px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-3 justify-between items-center">
            <p className="text-xs text-gray-500">
              Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)
            </p>
            <Button
              onClick={onClose}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold px-6"
            >
              Entendi
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
